import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme, CssBaseline, AppBar, Toolbar, Typography, Button, Box, Container, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { YouTube, Dashboard, Settings, Podcasts, Favorite, ShoppingCart, Forum, MusicVideo, CameraAlt, VpnKey, OpenInNew, Public } from '@mui/icons-material';
import MasterDashboard from './components/MasterDashboard';
import YouTubeDashboard from './components/YouTubeDashboard';
import MegaphoneDashboard from './components/MegaphoneDashboard';
import PatreonDashboard from './components/PatreonDashboard';
import ShopifyDashboard from './components/ShopifyDashboard';
import RedditDashboard from './components/RedditDashboard';
import TikTokDashboard from './components/TikTokDashboard';
import MetaDashboard from './components/MetaDashboard';
import WebsiteDashboard from './components/WebsiteDashboard';
import AdminDashboard from './components/AdminDashboard';
import AdminLoginDialog from './components/admin/AdminLoginDialog';
import LoginGate from './components/LoginGate';
import ErrorBoundary from './components/ErrorBoundary';
import MegaphoneTokenBanner from './components/MegaphoneTokenBanner';
import { useUrlState } from './hooks/useUrlState';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// CRT Terminal theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00ff41',
    },
    secondary: {
      main: '#ff6600',
    },
    background: {
      default: '#0a0a0a',
      paper: '#0f0f0f',
    },
    text: {
      primary: '#00ff41',
      secondary: '#5aaa5a',
    },
    divider: '#1e3a1e',
    error: { main: '#ff3344' },
    warning: { main: '#ffb000' },
    info: { main: '#00ffff' },
    success: { main: '#00ff41' },
  },
  typography: {
    fontFamily: "'VT323', 'Share Tech Mono', 'Courier New', monospace",
    h4: { fontFamily: "'Press Start 2P', monospace", fontSize: '16px', letterSpacing: '0.06em' },
    h5: { fontFamily: "'Press Start 2P', monospace", fontSize: '13px', letterSpacing: '0.06em' },
    h6: { fontFamily: "'Press Start 2P', monospace", fontSize: '11px', letterSpacing: '0.06em' },
    body1: { fontSize: '18px' },
    body2: { fontSize: '16px' },
    caption: { fontSize: '14px', color: '#5aaa5a' },
    button: { fontFamily: "'Press Start 2P', monospace", fontSize: '9px', letterSpacing: '0.08em' },
  },
  shape: {
    borderRadius: 0,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#0a0a0a' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid #1e3a1e',
        },
      },
    },
    // Mobile padding: keep desktop generous, give phones their screen back.
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            paddingLeft: 8,
            paddingRight: 8,
          },
        }),
      },
    },
    // Touch targets: bump to 44px on phones (Apple HIG minimum).
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minWidth: 44,
            minHeight: 44,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minHeight: 44,
          },
        }),
      },
    },
    // Tables on phones: smaller cell padding so we waste less width.
    MuiTableCell: {
      styleOverrides: {
        sizeSmall: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            padding: '6px 6px',
            fontSize: '13px',
          },
        }),
      },
    },
  },
});

function App() {
  // currentView mirrored in the URL as ?view=megaphone (etc.) so a
  // refresh / browser-back lands you in the same dashboard. Default
  // 'master' is omitted from the URL (clean root path).
  type View = 'youtube' | 'megaphone' | 'patreon' | 'shopify' | 'reddit' | 'tiktok' | 'meta' | 'master' | 'website' | 'admin';
  const [currentViewStr, setCurrentViewStr] = useUrlState('view', 'master');
  const currentView = currentViewStr as View;
  const setCurrentView = (v: View) => setCurrentViewStr(v);
  // Deep-link target for the MUM token banner → MUM Setup tab + dead site,
  // scrolled to the paste form. nonce makes each click re-fire the effect.
  const [mumDeep, setMumDeep] = useState<{ site: string; nonce: number } | null>(null);
  const [adminAnchor, setAdminAnchor] = useState<null | HTMLElement>(null);
  const adminMenuOpen = Boolean(adminAnchor);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* Animated CRT grid backdrop — fixed, pointer-events:none.
            Sits behind every later sibling in source order. Adds depth
            + a soft contrast surface so panels/cards have something
            besides pure black to sit against. See .crt-grid in
            index.css for the scrolling animation. */}
        <div className="crt-grid" aria-hidden="true" />
        <LoginGate apiUrl={apiUrl}>
        <AppBar position="static" elevation={0}>
          <Toolbar sx={{ gap: 2, flexWrap: 'nowrap' }}>
            <IconButton
              aria-label="admin menu"
              aria-controls={adminMenuOpen ? 'admin-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={adminMenuOpen ? 'true' : undefined}
              onClick={(e) => setAdminAnchor(e.currentTarget)}
              sx={{
                color: 'primary.main',
                border: '1px solid #1e3a1e',
                borderRadius: 0,
                mr: 1,
                '&:hover': { borderColor: '#00ff41', bgcolor: 'rgba(0,255,65,0.08)' },
              }}
            >
              <Settings fontSize="small" />
            </IconButton>
            <Menu
              id="admin-menu"
              anchorEl={adminAnchor}
              open={adminMenuOpen}
              onClose={() => setAdminAnchor(null)}
              MenuListProps={{ dense: true, sx: { py: 0.5 } }}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: 'background.paper',
                    border: '1px solid #1e3a1e',
                    borderRadius: 0,
                    minWidth: 260,
                    color: 'primary.main',
                    fontFamily: "'VT323', monospace",
                  },
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{ px: 2, py: 1, display: 'block', fontFamily: "'Press Start 2P', monospace", fontSize: 9 }}
              >
                &gt; ADMIN PANEL
              </Typography>
              <Divider sx={{ borderColor: '#1e3a1e' }} />
              <MenuItem
                component="a"
                href={`${apiUrl}/oauth/status`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setAdminAnchor(null)}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                  <VpnKey fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="[ OAUTH TOKENS ]"
                  secondary="Re-auth YouTube, check expiry"
                  primaryTypographyProps={{ sx: { fontFamily: "'Press Start 2P', monospace", fontSize: 9 } }}
                  secondaryTypographyProps={{ sx: { color: 'text.secondary', fontSize: 12 } }}
                />
                <OpenInNew fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
              </MenuItem>
              <MenuItem
                onClick={() => { setCurrentView('admin'); setAdminAnchor(null); }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                  <Dashboard fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="[ ADMIN DASHBOARD ]"
                  secondary="ETL jobs, alerts, audit log"
                  primaryTypographyProps={{ sx: { fontFamily: "'Press Start 2P', monospace", fontSize: 9 } }}
                  secondaryTypographyProps={{ sx: { color: 'text.secondary', fontSize: 12 } }}
                />
              </MenuItem>
              <MenuItem
                component="a"
                href="https://jeeves.myknowledgeproject.org/production"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setAdminAnchor(null)}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                  <Dashboard fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="[ PRODUCTION BOARD ]"
                  secondary="jeeves pipeline (source of truth)"
                  primaryTypographyProps={{ sx: { fontFamily: "'Press Start 2P', monospace", fontSize: 9 } }}
                  secondaryTypographyProps={{ sx: { color: 'text.secondary', fontSize: 12 } }}
                />
                <OpenInNew fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
              </MenuItem>
            </Menu>
            <Typography
              variant="h6"
              component="div"
              sx={{
                flexShrink: 0, whiteSpace: 'nowrap', mr: 2,
                // Truncate title on phones — saves ~140px of header width.
                fontSize: { xs: '10px', sm: '11px' },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                &gt; AUTOPOD // ANALYTICS_
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                &gt; AP_
              </Box>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1.25,
                flex: 1,
                minWidth: 0,
                overflowX: 'auto',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { background: '#1e3a1e' },
                // keep every button on a single line
                '& .MuiButton-root': { whiteSpace: 'nowrap', flexShrink: 0 },
              }}
            >
              {([
                { key: 'master',    label: '[ MASTER ]',   icon: <Dashboard /> },
                { key: 'youtube',   label: '[ YOUTUBE ]',  icon: <YouTube /> },
                { key: 'megaphone', label: '[ PODCAST ]',  icon: <Podcasts /> },
                { key: 'patreon',   label: '[ PATREON ]',  icon: <Favorite /> },
                { key: 'shopify',   label: '[ SHOPIFY ]',  icon: <ShoppingCart /> },
                { key: 'reddit',    label: '[ REDDIT ]',   icon: <Forum /> },
                { key: 'tiktok',    label: '[ TIKTOK ]',   icon: <MusicVideo /> },
                { key: 'meta',      label: '[ META ]',     icon: <CameraAlt /> },
                { key: 'website',   label: '[ WEBSITE ]',  icon: <Public /> },
                { key: 'admin',     label: '[ ADMIN ]',    icon: <Settings /> },
              ] as const).map((t) => (
                <Button
                  key={t.key}
                  color="inherit"
                  startIcon={t.icon}
                  variant={currentView === t.key ? 'outlined' : 'text'}
                  onClick={() => setCurrentView(t.key as typeof currentView)}
                  sx={{
                    // On phones drop the bracketed text label — icons only.
                    // The icon stays as `startIcon`, which shows fine on its
                    // own when there's no text next to it.
                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                    minWidth: { xs: 44, sm: 'auto' },
                    px: { xs: 1, sm: 1.5 },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {t.label}
                  </Box>
                </Button>
              ))}
            </Box>
          </Toolbar>
        </AppBar>
        <MegaphoneTokenBanner
          onFix={(slug) => { setMumDeep({ site: slug, nonce: Date.now() }); setCurrentView('megaphone'); }}
        />
        <ErrorBoundary>
          {currentView === 'youtube' && <YouTubeDashboard />}
          {currentView === 'megaphone' && <MegaphoneDashboard mumDeep={mumDeep} />}
          {currentView === 'patreon' && <PatreonDashboard />}
          {currentView === 'shopify' && <ShopifyDashboard />}
          {currentView === 'reddit' && <RedditDashboard />}
          {currentView === 'tiktok' && <TikTokDashboard />}
          {currentView === 'meta' && <MetaDashboard />}
          {/* `as string` cast handles legacy ?view=seo / ?view=ga4 URLs
              that still float around in bookmarks — both land on the
              new Website dashboard now. */}
          {(currentView === 'website'
            || (currentViewStr as string) === 'seo'
            || (currentViewStr as string) === 'ga4') && <WebsiteDashboard />}
          {currentView === 'master' && <MasterDashboard onNavigate={(v) => setCurrentView(v as typeof currentView)} />}
          {currentView === 'admin' && <AdminDashboard />}
        </ErrorBoundary>
        {/* Global admin-token dialog — listens for 401 events from
            ANY admin-gated endpoint, not just the AdminDashboard. Lets
            the user paste their token from anywhere in the app (e.g.
            mid-Megaphone-publish wizard). */}
        <AdminLoginDialog />
        </LoginGate>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
